// src/components/SectorROIInsights.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Badge,
  Divider,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import { getRoiBadge, getTrendIcon } from "./flyer/roiUtils";

/**
 * SectorROIInsights
 * Displays ROI insights for postcode sectors tied to the current business.
 * - Reads from campaign_intelligence_cache using businessId (uuid).
 * - ROI is now "expected redemptions" based on households × 2.8%.
 */
export default function SectorROIInsights({ businessId }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!businessId) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("campaign_intelligence_cache")
          .select(
            "postcode_sector, roi_estimate, roi_estimate_prev_14d, redemption_velocity_7d"
          )
          .eq("business_id", businessId)
          .order("roi_estimate", { ascending: false });

        if (error) throw error;
        setInsights(data || []);
      } catch (err) {
        console.error("Failed to fetch ROI insights:", err);
        setInsights([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [businessId]);

  return (
    <Box p={4} bg="gray.50" borderRadius="md">
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold">📈 Sector ROI Insights</Text>
        <Badge colorScheme="green" variant="subtle">
          Live Intelligence
        </Badge>
      </Flex>

      {loading ? (
        <Spinner />
      ) : insights.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No ROI data available.
        </Text>
      ) : (
        <VStack align="start" spacing={3}>
          {insights.map((s) => {
            const currentRoi = s.roi_estimate || 0;
            const previousRoi = s.roi_estimate_prev_14d || 0;
            const roiBadge = getRoiBadge(currentRoi);
            const trendIcon = getTrendIcon(currentRoi, previousRoi);

            return (
              <Box key={s.postcode_sector}>
                <Tooltip
                  label={`Velocity: ${
                    s.redemption_velocity_7d ?? 0
                  } scans/week • Expected redemptions: ${currentRoi.toFixed(0)}`}
                  hasArrow
                  placement="top"
                >
                  <Text fontSize="sm">
                    <strong>{s.postcode_sector}</strong> — ROI (expected
                    redemptions):{" "}
                    {s.roi_estimate !== null && s.roi_estimate !== undefined
                      ? currentRoi.toFixed(0)
                      : "—"}{" "}
                    {trendIcon}
                    <Badge ml={2} backgroundColor={roiBadge.color} color="white">
                      {roiBadge.label}
                    </Badge>
                  </Text>
                </Tooltip>
                <Divider />
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
